import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

import {
  createBalancePaymentLink,
  fetchedPlayer,
  getAccessibleBoards,
} from "../../features/player/playerSlice";

import { useAppDispatch, useAppSelector } from "../../store";
import { admin } from "../../features/session/sessionSlice";
import { isPreRegistrationEntryFlow } from "../../utils/registerFlow";
import {
  Typography,
  Box,
  Alert,
  Stack,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Zoom,
  useTheme,
  Snackbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import firestoreInstance from "../../services/firestoreInstance";
import { Participation } from "../../types/Participation";
import { computePlayerRegistrationBalance } from "../../helpers/playerRegistrationBalance";
import BalancePaymentLinkDialog, {
  BalancePaymentLinkPreview,
} from "../Admin/BalancePaymentLinkDialog";

interface RegisterValidationProps {}

const RegisterValidation: FC<RegisterValidationProps> = () => {
  const dispatch = useAppDispatch();
  const player = useAppSelector(fetchedPlayer);
  const boards = useAppSelector(getAccessibleBoards);
  const isAdmin = useAppSelector(admin);
  const tournamentId = useAppSelector((state) => state.tournoi.tournoi?.id);
  const pricingTag = useAppSelector((state) => state.tournoi.tournoi?.pricingTag);
  const isPreRegistrationEntry = isPreRegistrationEntryFlow();
  const entryRoute =
    !isAdmin && isPreRegistrationEntry
      ? "/pre-registration"
      : "/register";
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [enrichedParticipations, setEnrichedParticipations] = useState<
    Participation[]
  >([]);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [balanceLinkLoading, setBalanceLinkLoading] = useState(false);
  const [balancePreview, setBalancePreview] =
    useState<BalancePaymentLinkPreview | null>(null);
  const [linkSnackBar, setLinkSnackBar] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({ open: false, severity: "success", message: "" });

  const sortedParticipations = [...(player?.participation || [])].sort(
    (a, b) => {
      const boardA = boards.find((board) => board.id === a.tableauId);
      const boardB = boards.find((board) => board.id === b.tableauId);

      if (boardA && boardB) {
        return boardA.name.localeCompare(boardB.name);
      }
      return 0;
    }
  );

  useEffect(() => {
    if (!isAdmin || !tournamentId || !player?.participation?.length) {
      setEnrichedParticipations([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      const rows = await Promise.all(
        player.participation!.map(async (p) => {
          const tableau =
            boards.find((b) => b.id === p.tableauId) ?? p.tableau;
          let paymentStatus = p.paymentStatus;
          if (p.id && tournamentId) {
            try {
              const privateSnap = await getDoc(
                doc(
                  firestoreInstance,
                  "tournois",
                  tournamentId,
                  "participation",
                  String(p.id),
                  "private",
                  "data"
                )
              );
              if (privateSnap.exists()) {
                const data = privateSnap.data() as {
                  paymentStatus?: Participation["paymentStatus"];
                };
                paymentStatus = data.paymentStatus;
              }
            } catch {
              // garde le statut déjà présent si lecture privée impossible
            }
          }
          return {
            ...p,
            tableau,
            paymentStatus,
            players: player,
          } as Participation;
        })
      );
      if (!cancelled) {
        setEnrichedParticipations(rows.filter((r) => r.tableau));
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, tournamentId, player, boards]);

  const playerBalance = useMemo(
    () =>
      computePlayerRegistrationBalance(enrichedParticipations, pricingTag),
    [enrichedParticipations, pricingTag]
  );

  const openBalanceLinkDialog = () => {
    if (!player?.id || playerBalance.dueEuros <= 0) return;
    setBalancePreview({
      playerLabel: `${player.name} ${player.surname}`,
      paidBoardNames: playerBalance.paidBoardNames,
      unpaidBoardNames: playerBalance.unpaidBoardNames,
      totalEuros: playerBalance.totalEuros,
      paidEuros: playerBalance.paidEuros,
      dueEuros: playerBalance.dueEuros,
    });
    setBalanceDialogOpen(true);
  };

  const handleConfirmBalanceLink = async () => {
    if (!player?.id) return;
    setBalanceLinkLoading(true);
    try {
      const result = await dispatch(
        createBalancePaymentLink({ playerId: player.id })
      ).unwrap();
      await navigator.clipboard.writeText(result.url);
      setBalanceDialogOpen(false);
      setLinkSnackBar({
        open: true,
        severity: "success",
        message: `Lien Stripe ${result.amountEuros} € copié dans le presse-papier.`,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Impossible de générer le lien de paiement.";
      setLinkSnackBar({ open: true, severity: "error", message });
    } finally {
      setBalanceLinkLoading(false);
    }
  };

  return (
    <Zoom in>
      <Stack spacing={4} alignItems="center" maxWidth={800} mx="auto">
        <Alert
          severity="success"
          variant="outlined"
          sx={{
            borderColor: "success.main",
            borderLeft: `4px solid ${theme.palette.success.main}`,
            ...(isDark && {
              bgcolor: "rgba(76, 175, 80, 0.15)",
              color: "success.light",
            }),
          }}
        >
          Votre inscription a réussi.
        </Alert>

        <Typography
          variant="h5"
          fontWeight={600}
          gutterBottom
          sx={{
            color: isDark
              ? theme.palette.secondary.light
              : theme.palette.secondary.dark,
            textAlign: "center",
          }}
        >
          Vous êtes inscrit aux tableaux suivants :
        </Typography>

        <TableContainer
          component={Paper}
          sx={(t) => ({
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: t.palette.background.paper,
            border: "1px solid",
            borderColor: "divider",
            borderLeft: `4px solid ${t.palette.secondary.main}`,
            ...(t.palette.mode === "dark" && {
              boxShadow: `0 0 0 1px ${alpha(t.palette.secondary.main, 0.12)}`,
            }),
            "& .MuiTableCell-root": {
              color: t.palette.text.primary,
              borderColor: t.palette.divider,
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              fontWeight: 600,
              color: t.palette.text.primary,
            },
          })}
        >
          <Table aria-label="inscriptions">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell align="right">Nom</TableCell>
                <TableCell align="right">Jour</TableCell>
                <TableCell align="right">Fin pointage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedParticipations?.map((participation) => {
                const board = boards.find(
                  (board) => board.id === participation.tableauId
                );

                return (
                  <TableRow key={board?.name}>
                    <TableCell>{board?.name}</TableCell>
                    <TableCell align="right">{board?.label}</TableCell>
                    <TableCell align="right">
                      {new Date(board?.date ?? "").toLocaleDateString("fr-FR", {
                        dateStyle: "full",
                      })}
                    </TableCell>
                    <TableCell align="right">{board?.heureLimite}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {isAdmin && playerBalance.dueEuros > 0 && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ width: "100%" }}
            action={
              <Button
                color="secondary"
                variant="contained"
                size="small"
                onClick={openBalanceLinkDialog}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Lien Stripe {playerBalance.dueEuros} €
              </Button>
            }
          >
            Reste à payer : {playerBalance.dueEuros} € (solde sur l&apos;ensemble
            des tableaux du joueur).
          </Alert>
        )}

        <Box
          width="100%"
          mt={4}
          p={2}
          borderRadius={2}
          boxShadow={2}
          sx={(t) => ({
            bgcolor: t.palette.background.paper,
            border: "1px solid",
            borderColor: "divider",
            borderLeft: `4px solid ${t.palette.secondary.main}`,
          })}
        >
          <Button
            onClick={() => navigate(entryRoute)}
            variant="contained"
            color="secondary"
            fullWidth
            sx={{
              transition: "transform 0.3s ease",
              fontWeight: 600,
              "&:hover": {
                transform: "translateY(-2px)",
                ...(isDark && {
                  backgroundColor: "secondary.dark",
                  color: "#fff",
                }),
              },
              ...(isDark && { color: "#fff" }),
            }}
            startIcon={<span style={{ fontSize: "1.2rem" }}>➕</span>}
          >
            Inscrire un autre joueur
          </Button>
        </Box>

        <BalancePaymentLinkDialog
          open={balanceDialogOpen}
          preview={balancePreview}
          loading={balanceLinkLoading}
          onClose={() => {
            if (!balanceLinkLoading) {
              setBalanceDialogOpen(false);
            }
          }}
          onConfirm={handleConfirmBalanceLink}
        />
        <Snackbar
          open={linkSnackBar.open}
          autoHideDuration={6000}
          anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
          onClose={() => setLinkSnackBar((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setLinkSnackBar((s) => ({ ...s, open: false }))}
            severity={linkSnackBar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {linkSnackBar.message}
          </Alert>
        </Snackbar>
      </Stack>
    </Zoom>
  );
};

export default RegisterValidation;
